import type { Ref } from 'vue'
import type { SubjectCode } from '~/models/Subject'
import type { ContentEvent, ContentEventData } from '~/models/activity/ActivityDTO'
import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { activityEventClient } from '~/api/client/activityEventClient'

type ProvidedTimestamp = DateTime

type CreateContentEventInput = Omit<ContentEventData, 'providedTimestamp' | 'userId'> & {
  providedTimestamp?: ProvidedTimestamp
}

const TYPE = 'CONTENT_EVENT' as const

const toIsoSeconds = (dt: DateTime): string => {
  const seconds = dt.startOf('second')
  return seconds.toISO({ suppressMilliseconds: true, includeOffset: false })!
}

const normalizeIsoSeconds = (ts?: ProvidedTimestamp): string => {
  const dt = ts ?? DateTime.now()
  if (!dt.isValid) {
    console.warn('providedTimestamp must be a valid Luxon DateTime, using current time as fallback')
    return toIsoSeconds(DateTime.now())
  }
  return toIsoSeconds(dt)
}

const assertFiniteNumber = (value: unknown, name: string): boolean => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    console.warn(`${name} must be a finite number, received:`, value)
    return false
  }
  return true
}

export const useContentEvent = () => {
  const { isStudent, userId } = storeToRefs(useAuthStore())
  const productStore = useProductStore()

  const createContentEvent = (input: CreateContentEventInput): ContentEvent | null => {
    const { contentId, locationId, subject, providedTimestamp } = input

    if (!isStudent.value) {
      console.warn('ContentEvent disabled for non-student users')
      return null
    }

    const userIdValue = userId.value
    if (!userIdValue) {
      console.warn('userId is required')
      return null
    }
    if (!subject) {
      console.warn('subject is required')
      return null
    }

    if (!assertFiniteNumber(contentId, 'contentId') || !assertFiniteNumber(locationId, 'locationId')) {
      return null
    }

    const event: ContentEvent = {
      type: TYPE,
      data: {
        userId: userIdValue,
        contentId,
        locationId,
        subject,
        providedTimestamp: normalizeIsoSeconds(providedTimestamp),
      },
    }

    return Object.freeze(event)
  }

  const inferSubjectCode = (
    item: { contentId: number, locationId: number, pathString?: string } | undefined,
  ): SubjectCode | undefined => {
    if (!item) {
      return undefined
    }

    if (item.pathString) {
      const productFromPath = productStore.getProductFromPathString(item.pathString)
      if (productFromPath?.subjects?.length) {
        return productFromPath.subjects[0]
      }
    }

    return undefined
  }

  const autoContentEvent = (
    itemRef: Ref<{ contentId: number, locationId: number, pathString?: string } | undefined>,
    onEvent?: (event: ContentEvent) => void,
  ) => {
    if (!isStudent.value) {
      return
    }

    watch(
      itemRef,
      (item) => {
        if (!item) {
          return
        }

        const subject = inferSubjectCode(item)
        if (!subject) {
          return
        }

        const event = createContentEvent({
          contentId: item.contentId,
          locationId: item.locationId,
          subject,
        })

        if (event === null) {
          return
        }

        if (typeof onEvent === 'function') {
          onEvent(event)
        } else {
          // Fire and forget, we don't care about the response
          void activityEventClient.post('/queue', event)
        }
      },
      { immediate: true },
    )
  }

  return {
    createContentEvent,
    autoContentEvent,
  }
}

export default useContentEvent
